import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { gurukulService } from '../../services';
import { fileToBase64, imageToWebPBase64 } from '../../utils/fileUtils';
import { getProvinces, getDistricts, getAllDistricts, getMunicipalities } from '../../constants/nepalLocations';
import PageHeader from '../../components/common/PageHeader';
import { API_BASE_URL } from '../../constants/config';
import CreatableSelect from 'react-select/creatable';
import { validateEmail, validatePassword, validatePhoneNumber } from '../../utils/validation';
import NepaliDate from 'nepali-date-converter';
import { NepaliDatePicker } from 'nepali-datepicker-reactjs';
import 'nepali-datepicker-reactjs/dist/index.css';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

const GurukulFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: paramId } = useParams();
    const { isAdmin, isCandidate, currentUserFullDetails } = useAuth();
    const { t } = useTranslation();
    const isSelfRegister = location.pathname === '/register-gurukul';

    // If admin, use paramId. If candidate, use linked candidateId.
    const id = paramId || (isCandidate() && currentUserFullDetails?.candidateId);
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Initialize tab from URL or default
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab');
    const [activeTab, setActiveTab] = useState(initialTab || 'basic');

    const [showPassword, setShowPassword] = useState(false);
    const submitLockRef = useRef(false);
    const initialEmailRef = useRef('');

    const tabsRef = useRef(null);

    // Auto-scroll on tab change
    useEffect(() => {
        if (tabsRef.current) {
            // Scroll to the tabs container so user sees the new content
            tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

            // Also scroll the active tab button into center view if it's overflowing (mobile)
            const activeTabButton = tabsRef.current.querySelector(`button[data-tab="${activeTab}"]`);
            if (activeTabButton) {
                activeTabButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [activeTab]);

    // Expenditure State
    const [expenditures, setExpenditures] = useState([]);
    const [loadingExpenditures, setLoadingExpenditures] = useState(false);
    const [expenditureForm, setExpenditureForm] = useState({
        title: '',
        description: '',
        images: []
    });
    const [expenditureFiles, setExpenditureFiles] = useState([]);
    const [expenditurePreview, setExpenditurePreview] = useState([]);

    const [formData, setFormData] = useState({
        // Basic Info
        name: '',
        nagarpalikaGaupalika: '', // Replacing constituency
        townName: '', // Town / Local area name
        district: '',
        province: '',
        bio: '', // Label: Description
        photoKey: '', // Label: Logo
        galleryImages: [], // Max 3
        establishedDate: '', // Replacing staphana_miti
        phone_number: '', // national digits
        phoneCountryCode: '+977',
        socialLinks: {
            facebook: '',
            twitter: '',
            linkedin: '',
            youtube: '',
            tiktok: '',
            instagram: '',
            website: ''
        },
        isEnabled: true,

        // Office & Education (Extended)
        permanentAddress: '', // Label: Mukhya Karyalaya
        sakhaKaryalaya: [''], // Array of strings
        sanchalitTaha: [], // Multiselect
        expertiseArea: '', // Label: Sewa Xetra
        padhaiSubject: [], // Multiselect
        areaOfInterest: '', // Label: Mukhya Sewa Xetra

        // Achievements (Third Tab)
        achievements: [], // Array of { title, description, image }

        // Specialties (Additional Info)
        specialties: [], // Array of { title, description }

        // Bank Details (Last Tab)
        bankDetails: {
            bank_name: '',
            branch: '',
            account_name: '',
            account_number: '',
            qr_code_key: ''
        },

        // Legacy/Auth fields
        email: '',
        password: '',

        // Branding
        backgroundImage: '',
        logo: '',
    });

    const [availableDistricts, setAvailableDistricts] = useState(getAllDistricts());
    const [availableMunicipalities, setAvailableMunicipalities] = useState([]);

    // Default option lists for multi-selects
    const defaultSanchalitOptions = ['Primary', 'Lower Secondary', 'Secondary', 'Higher Secondary', 'Bachelor', 'Master', 'PhD'];
    const defaultPadhaiOptions = ['Science', 'Management', 'Humanities', 'Education', 'Law', 'Engineering', 'Medical', 'Arts'];

    const provinces = getProvinces();
    const constituencyOptions = Array.from({ length: 15 }, (_, i) => i + 1);

    // Update available districts when province changes in formData
    useEffect(() => {
        if (formData.province) {
            const provinceDistricts = getDistricts(formData.province);
            setAvailableDistricts(provinceDistricts);
        } else {
            setAvailableDistricts(getAllDistricts());
        }
    }, [formData.province]);

    // Update available municipalities when province or district changes in formData
    useEffect(() => {
        if (formData.province && formData.district) {
            const munis = getMunicipalities(formData.province, formData.district);
            setAvailableMunicipalities(munis);

            // If current value is no longer valid for the selected province/district, clear it
            if (formData.nagarpalikaGaupalika && !munis.includes(formData.nagarpalikaGaupalika)) {
                setFormData(prev => ({ ...prev, nagarpalikaGaupalika: '' }));
            }
        } else {
            setAvailableMunicipalities([]);
            if (formData.nagarpalikaGaupalika) {
                setFormData(prev => ({ ...prev, nagarpalikaGaupalika: '' }));
            }
        }
    }, [formData.province, formData.district]);

    const { refreshProfile } = useAuth();

    const [photoPreview, setPhotoPreview] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [bgPreview, setBgPreview] = useState(null);

    // Established date (BS/AD)
    const [estDateMode, setEstDateMode] = useState('AD'); // AD | BS
    const [bsEstablishedDate, setBsEstablishedDate] = useState('');
    const [bsEstablishedDateError, setBsEstablishedDateError] = useState('');

    // Initial Data Fetching for Edit Mode
    useEffect(() => {
        if (isEditMode) {
            fetchCandidateData();
            fetchExpenditures();
        } else {
            // Reset form if not in edit mode (e.g. creating new or switched user)
            resetForm();
        }
    }, [id]);

    const resetForm = () => {
        initialEmailRef.current = '';
        setFormData({
            name: '',
            nagarpalikaGaupalika: '',
            townName: '',
            district: '',
            province: '',
            bio: '',
            photoKey: '',
            galleryImages: [],
            establishedDate: '',
            phone_number: '',
            phoneCountryCode: '+977',
            socialLinks: {
                facebook: '',
                twitter: '',
                linkedin: '',
                youtube: '',
                tiktok: '',
                instagram: '',
                website: ''
            },
            isEnabled: true,
            permanentAddress: '',
            sakhaKaryalaya: [''],
            sanchalitTaha: [],
            expertiseArea: '',
            padhaiSubject: [],
            areaOfInterest: '',
            achievements: [],
            specialties: [],
            bankDetails: {
                bank_name: '',
                branch: '',
                account_name: '',
                account_number: '',
                qr_code_key: ''
            },
            email: '',
            password: '',
            backgroundImage: '',
            logo: '',
        });
        setExpenditures([]);
        setPhotoPreview(null);
        setLogoPreview(null);
        setBgPreview(null);
        setEstDateMode('AD');
        setBsEstablishedDate('');
        setBsEstablishedDateError('');
        setError('');
        setSuccess('');
    };

    const toIsoDate = (jsDate) => {
        const y = jsDate.getFullYear();
        const m = String(jsDate.getMonth() + 1).padStart(2, '0');
        const d = String(jsDate.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const applyEstablishedDateBs = (bsValue) => {
        if (!bsValue) {
            setBsEstablishedDateError('');
            setFormData(prev => ({ ...prev, establishedDate: '' }));
            return;
        }
        try {
            const nep = new NepaliDate(bsValue);
            const js = nep.toJsDate();
            if (!(js instanceof Date) || Number.isNaN(js.getTime())) throw new Error('Invalid date');
            setBsEstablishedDateError('');
            setFormData(prev => ({ ...prev, establishedDate: toIsoDate(js) }));
        } catch (e) {
            setBsEstablishedDateError(t('donations.invalidBsDate'));
        }
    };

    const fetchExpenditures = async () => {
        if (!id) return;
        setLoadingExpenditures(true);
        try {
            const res = await gurukulService.getExpenditures(id);
            setExpenditures(res?.data || res || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingExpenditures(false);
        }
    };

    const normalizeArray = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
        return [];
    };

    const fetchCandidateData = async () => {
        setFetching(true);
        setError('');
        try {
            const response = await gurukulService.getGurukul(id);
            const data = response.data || response;

            // Backend stores images as base64 and returns them in both
            // the original field AND a *Url suffixed field (same value).
            // Use ?? fallback so either naming works.
            const logoImage = data.photoKeyUrl ?? data.photoKey ?? '';
            const galleryImages = data.galleryImagesUrl ?? data.galleryImages ?? [];
            const qrCodeKey = data.bankDetails
                ? (data.bankDetails.qr_code_keyUrl ?? data.bankDetails.qr_code_key ?? '')
                : '';

            const achievements = (data.achievements || []).map(ach => ({
                ...ach,
                image: ach.imageUrl ?? ach.image ?? ''
            }));

            const rawMobile = String(data.mobileNo || '').trim();
            const mobileMatch = rawMobile.match(/^\+(\d{1,3})(\d{6,14})$/);
            const phoneCountryCode = mobileMatch ? `+${mobileMatch[1]}` : '+977';
            const phoneNumber = mobileMatch ? mobileMatch[2] : rawMobile.replace(/\D/g, '');

            setFormData({
                name: data.name || '',
                nagarpalikaGaupalika: data.nagarpalikaGaupalika || '',
                townName: data.townName || '',
                district: data.district || '',
                bio: data.bio || '',
                photoKey: logoImage,
                galleryImages: galleryImages,
                province: data.province || '',
                isEnabled: data.isEnabled !== undefined ? data.isEnabled : true,
                establishedDate: (data.establishedDate || '').split('T')[0],
                phone_number: phoneNumber,
                phoneCountryCode,
                email: data.email || '',
                password: '',
                permanentAddress: data.permanentAddress || '',
                sakhaKaryalaya: normalizeArray(data.sakhaKaryalaya).length > 0
                    ? normalizeArray(data.sakhaKaryalaya)
                    : [''],
                sanchalitTaha: normalizeArray(data.sanchalitTaha),
                padhaiSubject: normalizeArray(data.padhaiSubject),
                areaOfInterest: data.areaOfInterest || '',
                socialLinks: data.socialLinks ? {
                    facebook: data.socialLinks.facebook || '',
                    twitter: data.socialLinks.twitter || '',
                    linkedin: data.socialLinks.linkedin || '',
                    youtube: data.socialLinks.youtube || '',
                    tiktok: data.socialLinks.tiktok || '',
                    instagram: data.socialLinks.instagram || '',
                    website: data.socialLinks.website || ''
                } : {
                    facebook: '',
                    twitter: '',
                    linkedin: '',
                    youtube: '',
                    tiktok: '',
                    instagram: '',
                    website: ''
                },
                achievements: achievements,
                specialties: data.specialties || [],
                bankDetails: {
                    bank_name: data.bankDetails?.bank_name || '',
                    branch: data.bankDetails?.branch || '',
                    account_name: data.bankDetails?.account_name || '',
                    account_number: data.bankDetails?.account_number || '',
                    qr_code_key: qrCodeKey
                },
                backgroundImage: data.backgroundImageUrl || '',
                logo: data.logoUrl || '',
            });
            initialEmailRef.current = data.email || '';

            setEstDateMode('AD');
            setBsEstablishedDate('');
            setBsEstablishedDateError('');

            // Pre-fill previews
            if (data.photoKeyUrl || data.photoKey) {
                setPhotoPreview(data.photoKeyUrl || data.photoKey);
            }
            if (data.logoUrl) {
                setLogoPreview(data.logoUrl);
            }
            if (data.backgroundImageUrl) {
                setBgPreview(data.backgroundImageUrl);
            }
        } catch (err) {
            setError('Failed to load gurukul data');
            console.error(err);
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContributionChange = (index, value) => {
        const newContributions = [...formData.major_contributions];
        newContributions[index] = value;
        setFormData(prev => ({ ...prev, major_contributions: newContributions }));
    };

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploading(true);
            try {
                const base64 = await imageToWebPBase64(file, 600, 800, 0.8);
                setFormData(prev => ({ ...prev, photoKey: base64 }));
                setPhotoPreview(base64);
            } catch (err) {
                setError('Failed to process photo');
            } finally {
                setUploading(false);
            }
        }
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploading(true);
            try {
                const base64 = await imageToWebPBase64(file, 400, 400, 0.8);
                setFormData(prev => ({ ...prev, logo: base64 }));
                setLogoPreview(base64);
            } catch (err) {
                setError('Failed to process logo');
            } finally {
                setUploading(false);
            }
        }
    };

    const handleBackgroundChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploading(true);
            try {
                const base64 = await imageToWebPBase64(file, 1600, 900, 0.7);
                setFormData(prev => ({ ...prev, backgroundImage: base64 }));
                setBgPreview(base64);
            } catch (err) {
                setError('Failed to process background image');
            } finally {
                setUploading(false);
            }
        }
    };



    // Expenditure Handlers
    const handleExpenditureChange = (e) => {
        const { name, value } = e.target;
        setExpenditureForm(prev => ({ ...prev, [name]: value }));
    };

    const handleExpenditureFilesChange = (e) => {
        const files = Array.from(e.target.files);
        setExpenditureFiles(files);

        // Previews
        const previews = files.map(file => URL.createObjectURL(file));
        setExpenditurePreview(previews);
    };

    const handleExpenditureSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); // Reuse main loading or create specific
        try {
            const imageKeys = [];
            if (expenditureFiles.length > 0) {
                for (const file of expenditureFiles) {
                    const base64 = await fileToBase64(file);
                    if (base64) {
                        imageKeys.push(base64);
                    }
                }
            }

            const payload = {
                ...expenditureForm,
                images: imageKeys
            };

            await gurukulService.createExpenditure(id, payload);
            setSuccess('Expenditure added successfully');

            // Reset form
            setExpenditureForm({ title: '', description: '', images: [] });
            setExpenditureFiles([]);
            setExpenditurePreview([]);

            // Refresh list
            fetchExpenditures();
        } catch (err) {
            console.error('Failed to add expenditure', err);
            setError('Failed to add expenditure');
        } finally {
            setLoading(false);
        }
    };
    const handleIssueChange = (index, field, value) => {
        const newItems = [...formData.issues_and_solutions];
        if (!newItems[index]) newItems[index] = { issue: '', solution: '' };
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, issues_and_solutions: newItems }));
    };
    const addIssue = () => {
        setFormData(prev => ({ ...prev, issues_and_solutions: [...prev.issues_and_solutions, { issue: '', solution: '' }] }));
    };
    const removeIssue = (index) => {
        setFormData(prev => ({ ...prev, issues_and_solutions: prev.issues_and_solutions.filter((_, i) => i !== index) }));
    };

    const handleScheduleChange = (index, field, value) => {
        const newItems = [...formData.campaign_schedule];
        if (!newItems[index]) newItems[index] = { date: '', event: '', location: '' };
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, campaign_schedule: newItems }));
    };
    const addSchedule = () => {
        setFormData(prev => ({ ...prev, campaign_schedule: [...prev.campaign_schedule, { date: '', event: '', location: '' }] }));
    };
    const removeSchedule = (index) => {
        setFormData(prev => ({ ...prev, campaign_schedule: prev.campaign_schedule.filter((_, i) => i !== index) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitLockRef.current) return;
        submitLockRef.current = true;
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            if (isSelfRegister) {
                const emailErr = validateEmail(formData.email || '');
                const passErr = validatePassword(formData.password || '');
                const phoneDigits = (formData.phone_number || '').replace(/\D/g, '');
                const phoneErr = validatePhoneNumber(phoneDigits) || (phoneDigits.length !== 10 ? t('auth.phoneTenDigits', 'Phone number must be 10 digits') : '');
                const bd = formData.bankDetails || {};
                const bankIncomplete = !bd.bank_name?.trim() || !bd.branch?.trim() || !bd.account_name?.trim() || !bd.account_number?.trim();
                if (emailErr || passErr || phoneErr || bankIncomplete) {
                    const parts = [emailErr, passErr, phoneErr].filter(Boolean);
                    if (bankIncomplete) {
                        parts.push(t('gurukul.form.registerValidation.bank'));
                    }
                    setError(parts.join(' '));
                    setLoading(false);
                    submitLockRef.current = false;
                    if (bankIncomplete) {
                        setActiveTab('bank');
                    }
                    return;
                }
            }

            // Determine the photo to send:
            // photoKey will be set to base64 if a new one was uploaded via handlePhotoChange
            // Otherwise, we don't need to send it (undefined will omitted).
            const photoKey = (photoPreview && photoPreview.startsWith('data:image'))
                ? photoPreview
                : undefined;

            // Gallery: keep all images that are non-empty strings (base64 or any valid value).
            // The backend stores and returns base64 directly, so we just pass through.
            const galleryImages = (formData.galleryImages || []).filter(img => img && img.length > 0);

            // Bank QR: send whatever value exists (base64 stored directly).
            const qrCodeKey = (formData.bankDetails?.qr_code_key && formData.bankDetails.qr_code_key.length > 0)
                ? formData.bankDetails.qr_code_key
                : undefined;

            const emailToSend = (isEditMode && !isSelfRegister && (formData.email || '') === (initialEmailRef.current || ''))
                ? undefined
                : (formData.email || '');

            const payload = {
                name: formData.name,
                nagarpalikaGaupalika: formData.nagarpalikaGaupalika,
                townName: formData.townName || '',
                district: formData.district,
                province: formData.province,
                bio: formData.bio,
                isEnabled: isSelfRegister ? false : formData.isEnabled,

                establishedDate: formData.establishedDate || null,
                mobileNo: `${formData.phoneCountryCode || ''}${(formData.phone_number || '').trim()}`,
                ...(emailToSend ? { email: emailToSend } : {}),
                permanentAddress: formData.permanentAddress || '',
                sakhaKaryalaya: formData.sakhaKaryalaya || [],
                sanchalitTaha: formData.sanchalitTaha || [],
                expertiseArea: formData.expertiseArea || '',
                padhaiSubject: formData.padhaiSubject || [],
                areaOfInterest: formData.areaOfInterest || '',

                socialLinks: formData.socialLinks || {},
                specialties: formData.specialties || [],
                achievements: (formData.achievements || []).map(ach => ({
                    title: ach.title,
                    description: ach.description,
                    // Pass the image as-is (backend stores base64 directly)
                    image: ach.image && ach.image.length > 0 ? ach.image : undefined
                })),

                galleryImages,
                photoKey: formData.photoKey || undefined,
                backgroundImage: formData.backgroundImage || undefined,
                logo: formData.logo || undefined,

                bankDetails: {
                    ...formData.bankDetails,
                    qr_code_key: qrCodeKey
                },

                ...(formData.password && { password: formData.password })
            };

            if (isSelfRegister) {
                await gurukulService.registerGurukulApplication(payload);
                setSuccess(t('gurukul.form.registerSuccess'));
                setTimeout(() => {
                    navigate('/login', {
                        state: { message: t('gurukul.form.registerRedirectMessage') },
                    });
                }, 1200);
            } else if (isEditMode) {
                await gurukulService.updateGurukul(id, payload);
                if (isCandidate()) {
                    await refreshProfile();
                }
                setSuccess('Gurukul updated successfully!');
            } else {
                await gurukulService.createGurukul(payload);
                setSuccess('Gurukul created successfully!');
            }

            if (!isSelfRegister && isAdmin()) {
                setTimeout(() => navigate('/admin/gurukuls'), 1500);
            } else if (!isSelfRegister) {
                window.scrollTo(0, 0);
            }

        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Failed to save gurukul');
        } finally {
            setLoading(false);
            submitLockRef.current = false;
        }
    };

    if (fetching) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-primary-50 pb-8">
            <PageHeader
                title={
                    isSelfRegister
                        ? t('gurukul.form.pageRegister')
                        : isEditMode
                            ? t('gurukul.form.pageEdit')
                            : t('gurukul.form.pageAdd')
                }
                subtitle={
                    isSelfRegister
                        ? t('gurukul.form.pageRegisterSub')
                        : isEditMode
                            ? t('gurukul.form.pageEditSub')
                            : t('gurukul.form.pageAddSub')
                }
                extra={
                    isSelfRegister ? (
                        <Link
                            to="/"
                            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-primary-600 transition-colors bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm"
                        >
                            {t('common.backToHome')}
                        </Link>
                    ) : (
                        isAdmin() && (
                            <button
                                onClick={() => navigate('/admin/gurukuls')}
                                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-primary-600 transition-colors bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm"
                            >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                {t('gurukul.form.backToList')}
                            </button>
                        )
                    )
                }
            />
            <div className="max-w-4xl mx-auto px-4">

                {isSelfRegister && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg text-sm">
                        {t('gurukul.form.registerIntro')}
                    </div>
                )}

                {error && <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">{error}</div>}
                {success && <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg border border-green-200">{success}</div>}

                {/* Tabs */}
                <div ref={tabsRef} className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto pb-2 scrollbar-hide">
                        {['basic', 'extended', 'achievements', 'specialties', 'bank'].map((tab) => (
                            <button
                                key={tab}
                                data-tab={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`${activeTab === tab
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
                            >
                                {tab === 'basic' ? t('gurukul.form.tab_basic') :
                                    tab === 'extended' ? t('gurukul.form.tab_extended') :
                                        tab === 'achievements' ? t('gurukul.form.tab_achievements') :
                                            tab === 'specialties' ? t('gurukul.form.tab_specialties') :
                                                tab === 'bank' ? t('gurukul.form.tab_bank') : tab}
                            </button>
                        ))}
                    </nav>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">

                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('gurukul.form.logoImage')}</label>
                                    <p className="text-xs text-gray-500 -mt-1 mb-2">{t('gurukul.form.imageRatio.logo', 'Recommended: 1:1 (square), clear logo')}</p>
                                    <div className="flex items-center gap-6">
                                        {logoPreview ? (
                                            <div className="relative group">
                                                <img src={logoPreview} alt="Logo Preview" className="h-24 w-24 rounded-full object-contain border-2 border-gray-200 bg-white" />
                                                <button
                                                    type="button"
                                                    onClick={() => { setFormData(prev => ({ ...prev, logo: '' })); setLogoPreview(null); }}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
                                                <span className="text-xs">{t('gurukul.form.noLogo')}</span>
                                            </div>
                                        )}
                                        <label className="cursor-pointer bg-white py-2 px-3 border border-primary-300 rounded-md shadow-sm text-sm leading-4 font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                            <span>{logoPreview ? t('gurukul.form.changeLogo') : t('gurukul.form.uploadLogo')}</span>
                                            <input type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('gurukul.form.backgroundImage')}</label>
                                    <p className="text-xs text-gray-500 -mt-1 mb-2">{t('gurukul.form.imageRatio.background', 'Recommended: 16:9 (landscape)')}</p>
                                    <div className="flex items-center gap-6">
                                        {bgPreview ? (
                                            <div className="relative group">
                                                <img src={bgPreview} alt="BG Preview" className="h-24 w-40 rounded object-cover border-2 border-gray-200 bg-white" />
                                                <button
                                                    type="button"
                                                    onClick={() => { setFormData(prev => ({ ...prev, backgroundImage: '' })); setBgPreview(null); }}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="h-24 w-40 rounded bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
                                                <span className="text-xs">{t('gurukul.form.noBackground')}</span>
                                            </div>
                                        )}
                                        <label className="cursor-pointer bg-white py-2 px-3 border border-primary-300 rounded-md shadow-sm text-sm leading-4 font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                            <span>{bgPreview ? t('gurukul.form.changeBG') : t('gurukul.form.uploadBG')}</span>
                                            <input type="file" className="hidden" accept="image/*" onChange={handleBackgroundChange} />
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('gurukul.form.profilePhoto')}</label>
                                    <p className="text-xs text-gray-500 -mt-1 mb-2">{t('gurukul.form.imageRatio.profile', 'Recommended: 4:3 or 1:1, min 800px')}</p>
                                    <div className="flex items-center gap-6">
                                        {photoPreview ? (
                                            <div className="relative group">
                                                <img src={photoPreview} alt="Profile Preview" className="h-24 w-24 rounded-lg object-cover border-2 border-gray-200 bg-white" />
                                                <button
                                                    type="button"
                                                    onClick={() => { setFormData(prev => ({ ...prev, photoKey: '' })); setPhotoPreview(null); }}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="h-24 w-24 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
                                                <span className="text-xs text-center">{t('actions.upload')}</span>
                                            </div>
                                        )}
                                        <label className="cursor-pointer bg-white py-2 px-3 border border-primary-300 rounded-md shadow-sm text-sm leading-4 font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                            <span>{photoPreview ? t('actions.edit') : t('actions.upload')}</span>
                                            <input type="file" className="hidden" accept="image/*" onChange={handlePhotoChange} />
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('gurukul.form.galleryImages')}</label>
                                    <p className="text-xs text-gray-500 -mt-1 mb-2">{t('gurukul.form.imageRatio.gallery', 'Up to 3 images. Recommended: 4:3')}</p>
                                    <div className="space-y-3">
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files).slice(0, 3 - formData.galleryImages.length);
                                                if (files.length === 0) return;

                                                setUploading(true);
                                                try {
                                                    const base64Images = [];
                                                    for (const file of files) {
                                                        const base64 = await fileToBase64(file);
                                                        base64Images.push(base64);
                                                    }
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        galleryImages: [...prev.galleryImages, ...base64Images].slice(0, 3)
                                                    }));
                                                } catch (err) {
                                                    setError('Failed to process gallery images');
                                                } finally {
                                                    setUploading(false);
                                                }
                                            }}
                                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                            disabled={formData.galleryImages.length >= 3}
                                        />
                                        {/* Gallery image previews — images are stored as base64 data URIs */}
                                        <div className="flex flex-wrap gap-3 mt-2">
                                            {formData.galleryImages.map((img, idx) => (
                                                <div key={idx} className="relative group">
                                                    {img ? (
                                                        <img
                                                            src={img}
                                                            alt={`Gallery ${idx + 1}`}
                                                            className="h-24 w-24 object-cover rounded border-2 border-gray-200 shadow-sm"
                                                        />
                                                    ) : (
                                                        <div className="h-24 w-24 rounded bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
                                                            <span className="text-xs text-center px-1">{t('gurukul.form.noLogo')}</span>
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({
                                                            ...prev,
                                                            galleryImages: prev.galleryImages.filter((_, i) => i !== idx)
                                                        }))}
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                                        title="Remove"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                    <span className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">{idx + 1}/3</span>
                                                </div>
                                            ))}
                                            {formData.galleryImages.length < 3 && (
                                                <div className="h-24 w-24 rounded border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400">
                                                    <span className="text-2xl leading-none">+</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.gurukulName')}</label>
                                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('gurukul.form.wardPlaceTole', 'वडा नं, ठाउँ, टोल')}
                                    </label>
                                    <input
                                        type="text"
                                        name="townName"
                                        value={formData.townName}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        placeholder={t('gurukul.form.wardPlaceTolePlaceholder', 'उदा. वडा १०, नयाँ बानेश्वर, अरुण टोल')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.province')}</label>
                                    <select
                                        name="province"
                                        value={formData.province}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    >
                                        <option value="">{t('gurukul.form.selectProvince')}</option>
                                        {provinces.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.district')}</label>
                                    <select
                                        name="district"
                                        value={formData.district}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                    >
                                        <option value="">{t('gurukul.form.selectDistrict')}</option>
                                        {availableDistricts.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.nagarpalikaGaupalika')}</label>
                                    <select
                                        name="nagarpalikaGaupalika"
                                        value={formData.nagarpalikaGaupalika}
                                        onChange={handleChange}
                                        required
                                        disabled={!formData.province || !formData.district}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 shadow-sm disabled:bg-gray-100 disabled:text-gray-500"
                                    >
                                        <option value="">
                                            {t('gurukul.form.selectNagarpalikaGaupalika') !== 'gurukul.form.selectNagarpalikaGaupalika'
                                                ? t('gurukul.form.selectNagarpalikaGaupalika')
                                                : 'नगरपालिका / गाउँपालिका छान्नुहोस्'}
                                        </option>
                                        {availableMunicipalities.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                    {!formData.province || !formData.district ? (
                                        <p className="mt-1 text-xs text-gray-500">{t('gurukul.form.muniDependsOnProvinceDistrict', 'पहिले प्रदेश र जिल्ला छान्नुहोस्')}</p>
                                    ) : null}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.staphana_miti')}</label>
                                    <div className="flex items-center gap-3">
                                        <div className="inline-flex rounded-xl overflow-hidden border border-gray-300 bg-gray-50">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEstDateMode('AD');
                                                    setBsEstablishedDateError('');
                                                }}
                                                className={`px-3 py-2 text-sm font-medium ${estDateMode === 'AD' ? 'bg-white text-gray-900' : 'text-gray-600'}`}
                                            >
                                                {t('donations.ad', 'AD')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEstDateMode('BS');
                                                    setBsEstablishedDateError('');
                                                    if (!bsEstablishedDate) {
                                                        const bs = new NepaliDate(new Date()).getBS();
                                                        const y = bs?.year;
                                                        const m = String((bs?.month ?? 0) + 1).padStart(2, '0');
                                                        const d = String(bs?.date ?? 1).padStart(2, '0');
                                                        setBsEstablishedDate(`${y}-${m}-${d}`);
                                                    }
                                                }}
                                                className={`px-3 py-2 text-sm font-medium ${estDateMode === 'BS' ? 'bg-white text-gray-900' : 'text-gray-600'}`}
                                            >
                                                {t('donations.bs', 'BS')}
                                            </button>
                                        </div>
                                    </div>

                                    {estDateMode === 'AD' ? (
                                        <input
                                            type="date"
                                            name="establishedDate"
                                            value={formData.establishedDate}
                                            onChange={handleChange}
                                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    ) : (
                                        <div className="mt-2 space-y-1">
                                            <NepaliDatePicker
                                                value={bsEstablishedDate}
                                                onChange={(value) => {
                                                    setBsEstablishedDate(value);
                                                    applyEstablishedDateBs(value);
                                                }}
                                                options={{ closeOnSelect: true }}
                                                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                                className="w-full"
                                            />
                                            <p className="text-xs text-gray-500">{t('donations.bsFormatHint')}</p>
                                            {bsEstablishedDateError ? (
                                                <p className="text-xs text-red-600">{bsEstablishedDateError}</p>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.phone')}</label>
                                    <div className="space-y-1">
                                        <PhoneInput
                                            defaultCountry="np"
                                            value={`${formData.phoneCountryCode || ''}${formData.phone_number || ''}`}
                                            onChange={(phone, meta) => {
                                                const dial = meta?.country?.dialCode ? `+${meta.country.dialCode}` : (formData.phoneCountryCode || '+977');
                                                const digitsOnly = String(phone || '').replace(/\D/g, '');
                                                const dialDigits = String(meta?.country?.dialCode || '').replace(/\D/g, '');
                                                const national = dialDigits && digitsOnly.startsWith(dialDigits)
                                                    ? digitsOnly.slice(dialDigits.length)
                                                    : digitsOnly;
                                                setFormData(prev => ({ ...prev, phoneCountryCode: dial, phone_number: national.slice(0, 10) }));
                                            }}
                                            inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <p className="text-xs text-gray-500">{t('auth.phoneTenDigits', '१० अंक मात्र (country code बाहेक)')}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.email')}</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleChange} required={isSelfRegister} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.password')}</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required={isSelfRegister}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            placeholder={isEditMode ? 'Leave blank to keep unchanged' : ''}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? (
                                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            ) : (
                                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                                {Object.keys(formData.socialLinks).map(platform => (
                                    <div key={platform}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 capitalize text-primary-600 font-bold">{platform}</label>
                                        <input
                                            type="url"
                                            value={formData.socialLinks[platform]}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                socialLinks: { ...prev.socialLinks, [platform]: e.target.value }
                                            }))}
                                            placeholder={`https://${platform}.com/...`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-xs"
                                        />
                                    </div>
                                ))}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.description')}</label>
                                <textarea name="bio" value={formData.bio} onChange={handleChange} required rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('gurukul.form.descriptionPlaceholder')}></textarea>
                            </div>

                            {!isSelfRegister && (
                                <div className="flex items-center">
                                    <input type="checkbox" id="isEnabled" name="isEnabled" checked={formData.isEnabled} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                    <label htmlFor="isEnabled" className="ml-2 block text-sm text-gray-900">{t('gurukul.form.enablePublic')}</label>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Office & Education Tab */}
                    {activeTab === 'extended' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.mukhya_karyalaya')}</label>
                                    <textarea name="permanentAddress" value={formData.permanentAddress} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Full address of main office..." />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">{t('gurukul.form.sakha_karyalaya')}</label>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, sakhaKaryalaya: [...prev.sakhaKaryalaya, ''] }))}
                                            className="text-xs bg-primary-50 text-primary-600 px-2 py-1 rounded border border-primary-200"
                                        >
                                            + Add Branch
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {formData.sakhaKaryalaya.map((branch, idx) => (
                                            <div key={idx} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={branch}
                                                    onChange={(e) => {
                                                        const newBranches = [...formData.sakhaKaryalaya];
                                                        newBranches[idx] = e.target.value;
                                                        setFormData(prev => ({ ...prev, sakhaKaryalaya: newBranches }));
                                                    }}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                    placeholder="Branch address..."
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, sakhaKaryalaya: prev.sakhaKaryalaya.filter((_, i) => i !== idx) }))}
                                                    className="text-red-500 px-2"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Sanchalit Taha - Creatable MultiSelect (react-select/creatable) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('gurukul.form.sanchalit_taha')}
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t('gurukul.form.sanchalit_taha_help', 'Select one or more levels, or type to create your own.')}
                                    </p>
                                    <CreatableSelect
                                        isMulti
                                        classNamePrefix="gurukul-multi"
                                        placeholder={t('gurukul.form.sanchalit_taha_placeholder', 'Select or type levels...')}
                                        value={(formData.sanchalitTaha || []).map(v => ({
                                            value: v,
                                            label: t(`gurukul.levels.${v.replace(/\s+/g, '_')}`, v)
                                        }))}
                                        onChange={(newValue) => {
                                            const values = (newValue || []).map(opt => opt.value);
                                            setFormData(prev => ({
                                                ...prev,
                                                sanchalitTaha: values
                                            }));
                                        }}
                                        options={Array.from(new Set([
                                            ...defaultSanchalitOptions,
                                            ...(formData.sanchalitTaha || [])
                                        ])).map(v => ({
                                            value: v,
                                            label: t(`gurukul.levels.${v.replace(/\s+/g, '_')}`, v)
                                        }))}
                                        formatCreateLabel={(inputValue) => t('common.createOption', { option: inputValue }) || `Create "${inputValue}"`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.sewa_xetra')}</label>
                                    <input
                                        type="text"
                                        name="expertiseArea"
                                        value={formData.expertiseArea}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="e.g. Education, Health..."
                                    />
                                </div>

                                {/* Padhai Subject - Creatable MultiSelect */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('gurukul.form.padhai_subject')}
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t('gurukul.form.padhai_subject_help', 'Select one or more subjects, or type to create your own.')}
                                    </p>
                                    <CreatableSelect
                                        isMulti
                                        classNamePrefix="gurukul-multi"
                                        placeholder={t('gurukul.form.padhai_subject_placeholder', 'Select or type subjects...')}
                                        value={(formData.padhaiSubject || []).map(v => ({
                                            value: v,
                                            label: t(`gurukul.subjects.${v}`, v)
                                        }))}
                                        onChange={(newValue) => {
                                            const values = (newValue || []).map(opt => opt.value);
                                            setFormData(prev => ({
                                                ...prev,
                                                padhaiSubject: values
                                            }));
                                        }}
                                        options={Array.from(new Set([
                                            ...defaultPadhaiOptions,
                                            ...(formData.padhaiSubject || [])
                                        ])).map(v => ({
                                            value: v,
                                            label: t(`gurukul.subjects.${v}`, v)
                                        }))}
                                        formatCreateLabel={(inputValue) => t('common.createOption', { option: inputValue }) || `Create "${inputValue}"`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.mukhya_sewa_xetra')}</label>
                                    <input
                                        type="text"
                                        name="areaOfInterest"
                                        value={formData.areaOfInterest}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Our Achievements Tab */}
                    {activeTab === 'achievements' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex justify-between items-center border-b pb-2">
                                <h3 className="text-lg font-medium text-gray-900 capitalize">{t('gurukul.achievements')}</h3>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        achievements: [...prev.achievements, { title: '', description: '', image: '' }]
                                    }))}
                                    className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 flex items-center"
                                >
                                    <span className="mr-1">+</span> {t('gurukul.form.addAchievement')}
                                </button>
                            </div>

                            <div className="space-y-8">
                                {formData.achievements.map((item, index) => (
                                    <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200 relative group">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                achievements: prev.achievements.filter((_, i) => i !== index)
                                            }))}
                                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="md:col-span-2 space-y-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('gurukul.form.achievementTitle')}</label>
                                                    <input
                                                        type="text"
                                                        value={item.title}
                                                        onChange={(e) => {
                                                            const newItems = [...formData.achievements];
                                                            newItems[index].title = e.target.value;
                                                            setFormData(prev => ({ ...prev, achievements: newItems }));
                                                        }}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="Title of achievement..."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('gurukul.form.achievementDescription')}</label>
                                                    <textarea
                                                        value={item.description}
                                                        onChange={(e) => {
                                                            const newItems = [...formData.achievements];
                                                            newItems[index].description = e.target.value;
                                                            setFormData(prev => ({ ...prev, achievements: newItems }));
                                                        }}
                                                        rows={3}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                        placeholder="Describe the achievement..."
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('gurukul.form.achievementImage')}</label>
                                                <p className="text-xs text-gray-500 -mt-1 mb-2">{t('gurukul.form.imageRatio.achievement', 'Recommended: 4:3')}</p>
                                                <div className="space-y-3">
                                                    {item.image ? (
                                                        <div className="relative">
                                                            {/* Images are stored as base64 data URIs — use directly as src */}
                                                            <img src={item.image} alt="Achievement" className="w-full h-32 object-cover rounded border" />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newItems = [...formData.achievements];
                                                                    newItems[index].image = '';
                                                                    setFormData(prev => ({ ...prev, achievements: newItems }));
                                                                }}
                                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                                                            >
                                                                &times;
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label className="w-full h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                            <span className="text-xs text-gray-500 mt-2">{t('gurukul.form.achievementImage')}</span>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                className="hidden"
                                                                onChange={async (e) => {
                                                                    const file = e.target.files[0];
                                                                    if (!file) return;
                                                                    setUploading(true);
                                                                    try {
                                                                        const base64 = await fileToBase64(file);
                                                                        const newItems = [...formData.achievements];
                                                                        newItems[index].image = base64;
                                                                        setFormData(prev => ({ ...prev, achievements: newItems }));
                                                                    } catch (err) {
                                                                        setError('Failed to process achievement image');
                                                                    } finally {
                                                                        setUploading(false);
                                                                        // allow re-selecting same file
                                                                        e.target.value = '';
                                                                    }
                                                                }}
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {formData.achievements.length === 0 && (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-gray-500">
                                        {t('gurukul.form.noAchievements')}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Gurukul Specialties Tab */}
                    {activeTab === 'specialties' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex justify-between items-center border-b pb-2">
                                <h3 className="text-lg font-medium text-gray-900">{t('gurukul.specialties')}</h3>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        specialties: [...prev.specialties, { title: '', description: '' }]
                                    }))}
                                    className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 flex items-center"
                                >
                                    <span className="mr-1">+</span> {t('gurukul.form.addSpecialty')}
                                </button>
                            </div>

                            <div className="space-y-4">
                                {formData.specialties.map((item, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex gap-4 items-start relative group">
                                        <div className="flex-1 space-y-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('gurukul.form.specialtyTitle')}</label>
                                                <input
                                                    type="text"
                                                    value={item.title}
                                                    onChange={(e) => {
                                                        const newItems = [...formData.specialties];
                                                        newItems[index].title = e.target.value;
                                                        setFormData(prev => ({ ...prev, specialties: newItems }));
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                    placeholder="e.g., Cultural Preservation"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
                                                <textarea
                                                    value={item.description}
                                                    onChange={(e) => {
                                                        const newItems = [...formData.specialties];
                                                        newItems[index].description = e.target.value;
                                                        setFormData(prev => ({ ...prev, specialties: newItems }));
                                                    }}
                                                    rows={2}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                    placeholder="Brief description of this specialty..."
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                specialties: prev.specialties.filter((_, i) => i !== index)
                                            }))}
                                            className="text-red-500 hover:text-red-700 mt-6"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                ))}

                                {formData.specialties.length === 0 && (
                                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-500">
                                        {t('gurukul.form.noSpecialties')}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Bank Details & QR Tab */}
                    {activeTab === 'bank' && (
                        <div className="space-y-8 animate-fadeIn">
                            <div className="bg-primary-50/50 p-6 rounded-lg border border-primary-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-6">{t('gurukul.form.bankSection')}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.bankName')}</label>
                                            <input
                                                type="text"
                                                value={formData.bankDetails.bank_name}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    bankDetails: { ...prev.bankDetails, bank_name: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                placeholder="e.g., Nepal Bank Ltd."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.branchName')}</label>
                                            <input
                                                type="text"
                                                value={formData.bankDetails.branch}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    bankDetails: { ...prev.bankDetails, branch: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                placeholder="e.g., Kathmandu Main Branch"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.accountHolder')}</label>
                                            <input
                                                type="text"
                                                value={formData.bankDetails.account_name}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    bankDetails: { ...prev.bankDetails, account_name: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                placeholder="Full name as in bank..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('gurukul.form.accountNumber')}</label>
                                            <input
                                                type="text"
                                                value={formData.bankDetails.account_number}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    bankDetails: { ...prev.bankDetails, account_number: e.target.value }
                                                }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-lg font-mono"
                                                placeholder="000-000000-00"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-xl bg-white">
                                        <label className="block text-sm font-bold text-gray-700 mb-4">{t('gurukul.form.qrCode')}</label>
                                        <p className="text-xs text-gray-500 -mt-3 mb-4">{t('gurukul.form.imageRatio.qr', 'Recommended: square (1:1). Keep code readable')}</p>

                                        {formData.bankDetails.qr_code_key ? (
                                            <div className="relative group">
                                                <img
                                                    src={formData.bankDetails.qr_code_key.startsWith('data:image') || formData.bankDetails.qr_code_key.startsWith('http') ? formData.bankDetails.qr_code_key : `${API_BASE_URL}/file/${formData.bankDetails.qr_code_key}`}
                                                    alt="Payment QR"
                                                    className="h-60 w-60 object-contain rounded border shadow-inner bg-white"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        bankDetails: { ...prev.bankDetails, qr_code_key: '' }
                                                    }))}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="flex flex-col items-center justify-center cursor-pointer space-y-2 p-8 text-center">
                                                <div className="bg-primary-50 p-4 rounded-full text-primary-600">
                                                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" /></svg>
                                                </div>
                                                <div className="text-sm font-medium text-gray-700">{t('gurukul.form.uploadQr')}</div>
                                                <div className="text-xs text-gray-500">{t('gurukul.form.qrFormats')}</div>
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        setUploading(true);
                                                        try {
                                                            const base64 = await fileToBase64(file);
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                bankDetails: { ...prev.bankDetails, qr_code_key: base64 }
                                                            }));
                                                        } catch (err) {
                                                            setError('Failed to process QR code image');
                                                        } finally {
                                                            setUploading(false);
                                                        }
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Gurukul Specialties Tab */}
                    {activeTab === 'additional' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="flex justify-between items-center border-b pb-2">
                                <h3 className="text-lg font-medium text-gray-900">Gurukul ko Bisestha (Specialties)</h3>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        specialties: [...prev.specialties, { title: '', description: '' }]
                                    }))}
                                    className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100 flex items-center"
                                >
                                    <span className="mr-1">+</span> Add Specialty
                                </button>
                            </div>

                            <div className="space-y-4">
                                {formData.specialties.map((item, index) => (
                                    <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex gap-4 items-start relative group">
                                        <div className="flex-1 space-y-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Title</label>
                                                <input
                                                    type="text"
                                                    value={item.title}
                                                    onChange={(e) => {
                                                        const newItems = [...formData.specialties];
                                                        newItems[index].title = e.target.value;
                                                        setFormData(prev => ({ ...prev, specialties: newItems }));
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                    placeholder="e.g., Cultural Preservation"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
                                                <textarea
                                                    value={item.description}
                                                    onChange={(e) => {
                                                        const newItems = [...formData.specialties];
                                                        newItems[index].description = e.target.value;
                                                        setFormData(prev => ({ ...prev, specialties: newItems }));
                                                    }}
                                                    rows={2}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                    placeholder="Brief description of this specialty..."
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                specialties: prev.specialties.filter((_, i) => i !== index)
                                            }))}
                                            className="text-red-500 hover:text-red-700 mt-6"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Expenditure Tab */}
                    {activeTab === 'expenditure' && (
                        <div className="space-y-8 animate-fadeIn">
                            {/* Add New Expenditure Form */}
                            <div className="bg-primary-50/50 p-6 rounded-lg border border-primary-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Expenditure</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={expenditureForm.title}
                                            onChange={handleExpenditureChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="e.g. Campaign Banner Printing"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea
                                            name="description"
                                            value={expenditureForm.description}
                                            onChange={handleExpenditureChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Details of the expenditure..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Images (Multiple)</label>
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            onChange={handleExpenditureFilesChange}
                                            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                        {expenditurePreview.length > 0 && (
                                            <div className="flex gap-2 mt-2 flex-wrap">
                                                {expenditurePreview.map((src, idx) => (
                                                    <img key={idx} src={src} alt="Preview" className="h-16 w-16 object-cover rounded border" />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button" // Use button type to prevent firing main form submit
                                            onClick={handleExpenditureSubmit}
                                            disabled={loading || !expenditureForm.title}
                                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                                        >
                                            {loading ? 'Adding...' : 'Add Expenditure'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* List Expenditures */}
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Expenditure History</h3>
                                {loadingExpenditures ? (
                                    <p>Loading...</p>
                                ) : expenditures.length === 0 ? (
                                    <p className="text-gray-500">No expenditures recorded yet.</p>
                                ) : (
                                    <div className="space-y-4">
                                        {expenditures.map((exp) => (
                                            <div key={exp.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-bold text-gray-800">{exp.title}</h4>
                                                        {exp.description && <p className="text-sm text-gray-600 mt-1">{exp.description}</p>}
                                                        <p className="text-xs text-gray-400 mt-2">{new Date(exp.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                {(exp.imagesUrl || exp.images) && (exp.imagesUrl || exp.images).length > 0 && (
                                                    <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                                                        {(exp.imagesUrl || exp.images).map((img, idx) => (
                                                            <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                                                                <img src={img} alt="Expenditure" className="h-20 w-auto object-cover rounded border hover:opacity-80 transition" />
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-8 flex gap-4 pt-4 border-t border-gray-200">
                        {isAdmin() && !isSelfRegister && (
                            <button type="button" onClick={() => navigate('/admin/gurukuls')} className="flex-1 px-4 py-3 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 font-medium">Cancel</button>
                        )}
                        <button type="submit" disabled={loading || uploading} className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm">
                            {loading || uploading
                                ? 'Processing...'
                                : isSelfRegister
                                    ? t('gurukul.form.submitApplication')
                                    : isEditMode
                                        ? 'Update Gurukul'
                                        : 'Create Gurukul'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default GurukulFormPage;
